import { forwardRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Node } from 'reactflow';
import { NodeData, Position } from '../../../pages/campaings/flows/types';
import Input from '../../forms/input.component';
import TextArea from '../../forms/text-area.component';
import Select from 'react-select';
import InvalidFeedback from '../../forms/invalid-feedback.component';
import Icon from '../../forms/icon.component';
import { Dropzone } from '@dropzone-ui/react';
import { useFilePreview } from '../../../hooks/use-file-preview';
import { AlertService } from '../../../services/alert.service';
import { AssetsServices } from '../../../services/assets.service';
import { getFilenameFromUrl } from '../flows-body/default-body.component';

interface DefaultNodeFormProps {
  node: Node<NodeData>;
  onSave: (nodeId: string, data: NodeData, position: Position) => void;
}

const MESSAGE_TYPE_OPTIONS = [
  { value: 'TEXT', label: 'Texto' },
  { value: 'IMAGE', label: 'Imagen' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Documento' }
] as const;

const DefaultNodeForm = forwardRef<HTMLFormElement, DefaultNodeFormProps>(
  ({ node, onSave }, ref) => {
    const {
      register,
      handleSubmit,
      control,
      watch,
      formState: { errors, isDirty }
    } = useForm<NodeData>({
      defaultValues: node.data,
      mode: 'onChange'
    });
    const buildPreview = (initial: string | null) => useFilePreview(null, initial);
    const defaultType = watch('default.type');

    const {
      file: imageFile,
      preview: imagePreview,
      setFile: setImageFile,
      setPreview: setImagePreview
    } = buildPreview(node.data.default?.image?.es?.link || null);

    const {
      file: videoFile,
      preview: videoPreview,
      setFile: setVideoFile,
      setPreview: setVideoPreview
    } = buildPreview(node.data.default?.video?.es?.link || null);

    const {
      file: docFile,
      preview: docPreview,
      setFile: setDocFile,
      setPreview: setDocPreview
    } = buildPreview(node.data.default?.document?.es?.link || null);

    const onSubmit = async (data: NodeData) => {
      if (!isDirty && ![imageFile, videoFile, docFile].some(Boolean))
        return AlertService.toastError('Debes de hacer un cambio antes de guardar');

      let finalLink: string | null = null;
      if (defaultType === 'IMAGE') {
        finalLink = imagePreview;
        if (imageFile) {
          const form = new FormData();
          form.append('file', imageFile);
          try {
            const uploadPromise = AssetsServices.uploadMedia(form);
            await AlertService.toastLoading(
              uploadPromise,
              'Subiendo imagen...',
              'La imagen se ha subido correctamente',
              'Ocurrió un error al subir la imagen, intenta nuevamente'
            );
            const resp = await uploadPromise;
            finalLink = resp.url;
          } catch (err: any) {
            console.error('Error subiendo imagen:', err);
            return AlertService.toastError(err.error || 'Error al subir la imagen');
          }
        }
        if (!finalLink) {
          return AlertService.toastError('Debes seleccionar una imagen antes de guardar.');
        }
        const caption = data.default?.image?.es?.caption?.trim();
        const imageEs: { link: string; caption?: string } = { link: finalLink };
        if (caption) {
          imageEs.caption = caption;
        }
        data.default = {
          type: 'IMAGE',
          image: { es: imageEs }
        };
      }
      if (defaultType === 'VIDEO') {
        finalLink = videoPreview;
        if (videoFile) {
          const form = new FormData();
          form.append('file', videoFile);
          try {
            const uploadPromise = AssetsServices.uploadMedia(form);
            await AlertService.toastLoading(
              uploadPromise,
              'Subiendo video...',
              'El video se ha subido correctamente',
              'Ocurrió un error al subir el video, intenta nuevamente'
            );
            const resp = await uploadPromise;
            finalLink = resp.url;
          } catch (e: any) {
            return AlertService.toastError(e.error || 'Error subiendo video');
          }
        }
        if (!finalLink) return AlertService.toastError('Selecciona un video antes de guardar');
        const caption = data.default?.video?.es?.caption?.trim();
        const videoEs: { link: string; caption?: string } = { link: finalLink };
        if (caption) {
          videoEs.caption = caption;
        }
        data.default = {
          type: 'VIDEO',
          video: { es: videoEs }
        };
      }

      if (defaultType === 'DOCUMENT') {
        finalLink = docPreview;
        if (docFile) {
          try {
            const form = new FormData();
            form.append('file', docFile);
            const uploadPromise = AssetsServices.uploadMedia(form);
            await AlertService.toastLoading(
              uploadPromise,
              'Subiendo documento...',
              'El documento se ha subido correctamente',
              'Ocurrió un error al subir el documento, intenta nuevamente'
            );
            const resp = await uploadPromise;
            finalLink = resp.url;
          } catch (e: any) {
            return AlertService.toastError(e.error || 'Error subiendo documento');
          }
        }
        if (!finalLink) return AlertService.toastError('Selecciona un documento antes de guardar');
        const caption = data.default?.document?.es?.caption?.trim();
        const documentEs: { link: string; caption?: string } = { link: finalLink };
        if (caption) {
          documentEs.caption = caption;
        }
        data.default = {
          type: 'DOCUMENT',
          document: { es: documentEs }
        };
      }
      if (defaultType === 'TEXT') {
        delete data.default?.file;
        delete data.default?.image;
        delete data.default?.video;
        delete data.default?.document;
      }
      onSave(node.id, data, node.position);
    };

    const fileSizeValidator = (file: File) => {
      if (file.size > 5 * 1024 * 1024) {
        AlertService.toastError('El archivo es demasiado grande. Máximo: 5 MB');
        return { valid: false, error: 'El archivo es demasiado grande. Máximo: 5 MB' };
      }
      return { valid: true };
    };

    return (
      <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-3">
          <Input
            label="Título del nodo"
            requiredIndicator="required"
            placeholder="Escribe el nombre del nodo"
            {...register('title', { required: 'Este campo es requerido' })}
            errors={errors.title?.message}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">
            Tipo de mensaje <span className="required"></span>
          </label>
          <Controller
            control={control}
            name="default.type"
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <Select
                  options={MESSAGE_TYPE_OPTIONS}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? `Sin opciones para '${inputValue}'` : 'Sin opciones'
                  }
                  menuPosition="fixed"
                  className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                  classNamePrefix="form-select-custom"
                  placeholder="Selecciona el tipo de contenido"
                  onChange={(selectedOption) => {
                    onChange(selectedOption ? selectedOption.value : '');
                  }}
                  value={MESSAGE_TYPE_OPTIONS.find((option) => option.value === value) || null}
                />
                <InvalidFeedback error={error} />
              </>
            )}
          />
        </div>

        <div className="mb-3">
          {defaultType === 'TEXT' && (
            <TextArea
              label="Mensaje"
              rows={4}
              {...register('default.text.es', {
                required: 'El mensaje es requerido',
                maxLength: { value: 1024, message: 'Máximo 1024 caracteres' }
              })}
              errors={errors.default?.text?.es?.message}
              placeholder="Escribe el mensaje"
              counter={1024}
              value={watch('default.text.es')?.length || 0}
              requiredIndicator="required"
            />
          )}
          {(defaultType === 'IMAGE' || defaultType === 'VIDEO' || defaultType === 'DOCUMENT') && (
            <div className="row mb-3">
              <div className="col-md-12">
                <div className="mb-3">
                  <Input
                    label="Leyenda / descripción"
                    placeholder="Escribe la leyenda"
                    {...register(`default.${defaultType.toLowerCase() as any}.es.caption` as never)}
                    errors={
                      errors.default?.[
                        defaultType.toLowerCase() as 'text' | 'image' | 'video' | 'document'
                      ]?.es?.message
                    }
                  />
                </div>
                <div className="fv-row">
                  <label className="form-label">
                    {defaultType === 'IMAGE'
                      ? 'Imagen'
                      : defaultType === 'VIDEO'
                        ? 'Video'
                        : 'Documento'}
                    <span className="required"></span>
                  </label>

                  <Controller
                    name="default.file"
                    control={control}
                    render={({ field: { onChange } }) => {
                      const file =
                        defaultType === 'IMAGE'
                          ? imageFile
                          : defaultType === 'VIDEO'
                            ? videoFile
                            : docFile;
                      const preview =
                        defaultType === 'IMAGE'
                          ? imagePreview
                          : defaultType === 'VIDEO'
                            ? videoPreview
                            : docPreview;
                      const setFile =
                        defaultType === 'IMAGE'
                          ? setImageFile
                          : defaultType === 'VIDEO'
                            ? setVideoFile
                            : setDocFile;
                      const setPreview =
                        defaultType === 'IMAGE'
                          ? setImagePreview
                          : defaultType === 'VIDEO'
                            ? setVideoPreview
                            : setDocPreview;

                      return file || preview ? (
                        <div className="text-center">
                          {defaultType === 'IMAGE' ? (
                            <img
                              src={preview || undefined}
                              alt="preview"
                              className="img-fluid rounded mb-2"
                              style={{ maxHeight: 150 }}
                            />
                          ) : (
                            <Icon icon="ki-file-image" size="fs-4x" />
                          )}
                          {defaultType === 'VIDEO' ? (
                            <video
                              src={preview || undefined}
                              style={{ maxHeight: 150 }}
                              controls
                              className="rounded"
                            />
                          ) : (
                            <Icon icon="ki-file-video" size="fs-4x" />
                          )}
                          {defaultType === 'DOCUMENT' ? (
                            <div className="rounded bg-light-primary p-4 d-flex align-items-center justify-content-center">
                              <Icon icon="ki-file-added" size="fs-2x" type="primary" />
                              <small className="ms-2">
                                {file?.name || getFilenameFromUrl(preview || '')}
                              </small>
                              <div className="ms-auto d-flex align-items-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => window.open(preview || '', '_blank')}
                                >
                                  {' '}
                                  Ver
                                </button>
                              </div>
                            </div>
                          ) : (
                            <Icon icon="ki-file-text" size="fs-4x" />
                          )}
                          <button
                            className="btn btn-danger btn-sm mt-2"
                            onClick={() => {
                              setFile(null);
                              setPreview(null);
                              onChange(null);
                            }}
                            type="button"
                          >
                            Eliminar
                          </button>
                        </div>
                      ) : (
                        <Dropzone
                          onChange={(incoming) => {
                            const valid = incoming.find((f) => f.valid);
                            if (valid) {
                              const f = valid.file as File | null;
                              setFile(f);
                              onChange(f);
                            } else {
                              setFile(null);
                              onChange(null);
                            }
                          }}
                          className="dropzone cursor-pointer"
                          accept={
                            defaultType === 'IMAGE'
                              ? '.png,.jpg,.jpeg,.gif'
                              : defaultType === 'VIDEO'
                                ? '.mp4,.mov,.webm'
                                : '.pdf,.doc,.docx'
                          }
                          maxFiles={1}
                          maxFileSize={5 * 1024 * 1024}
                          validator={fileSizeValidator}
                        >
                          <div className="dz-mkssage">
                            <Icon icon="ki-file-added" size="fs-5x" className="text-primary me-4" />
                            <div className="d-flex flex-column align-items-center flex-grow-1">
                              <div className="mb-3 mb-md-0 fw-semibold text-center">
                                <h5 className="text-gray-900 fw-bold">
                                  Agrega o sube un {defaultType.toLocaleLowerCase()}
                                </h5>
                                <div className="fs-7 pe-7 text-info">
                                  Sube tu {defaultType.toLocaleLowerCase()} <br />
                                  <small>Máx. 5 MB</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Dropzone>
                      );
                    }}
                  />
                  <InvalidFeedback error={errors.default?.file && 'Imagen requerida'} />
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    );
  }
);

export default DefaultNodeForm;
